/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'

import type { WithEuiThemeProps } from '@elastic/eui'
import {
  EuiButton,
  EuiButtonEmpty,
  EuiSkeletonText,
  EuiSpacer,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiOverlayMask,
  withEuiTheme,
  htmlIdGenerator,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { ClusterInstanceInfo } from '@modules/cloud-api/v1/types'
import type {
  Allocator,
  AnyResourceInfo,
  AsyncRequestState,
  SliderInstanceType,
} from '@modules/ui-types'
import { CuiAlert } from '@modules/cui/Alert'

import VacateNodesModal from '../../../Allocator/Vacate/VacateNodesModal'

type Props = {
  resource: AnyResourceInfo
  instance: ClusterInstanceInfo
  kind: SliderInstanceType
  allocator?: Allocator
  fetchAllocator: () => Promise<any>
  fetchAllocatorRequest: AsyncRequestState
  close: () => void
  onAfterVacate: () => void
}

class VacateNodeThroughAllocatorModal extends Component<Props & WithEuiThemeProps> {
  componentDidMount() {
    const { allocator, fetchAllocator } = this.props

    if (!allocator) {
      fetchAllocator()
    }
  }

  render() {
    const { allocator, resource, kind, close, onAfterVacate } = this.props

    if (!allocator) {
      return this.renderLoadingOverlay()
    }

    const clusterId = resource.id
    const nodes = [{ kind, clusterId }]

    return (
      <VacateNodesModal
        allocator={allocator}
        nodes={nodes}
        close={close}
        onAfterVacate={onAfterVacate}
      />
    )
  }

  renderLoadingOverlay() {
    const { fetchAllocatorRequest, close, theme } = this.props
    const modalTitleIdVacateLoading = htmlIdGenerator()('vacateLoadingModalTitle')

    return (
      <EuiOverlayMask>
        <EuiModal
          aria-labelledby={modalTitleIdVacateLoading}
          onClose={close}
          style={{ width: theme.euiTheme.breakpoint.m }}
        >
          <EuiModalHeader>
            <EuiModalHeaderTitle id={modalTitleIdVacateLoading}>
              <FormattedMessage
                id='vacate-single-node-modal.vacate-settings'
                defaultMessage='Move settings'
              />
            </EuiModalHeaderTitle>
          </EuiModalHeader>

          <EuiModalBody>
            {fetchAllocatorRequest.inProgress && <EuiSkeletonText />}

            {fetchAllocatorRequest.error && (
              <Fragment>
                <EuiSpacer size='m' />
                <CuiAlert type='error'>{fetchAllocatorRequest.error}</CuiAlert>
              </Fragment>
            )}
          </EuiModalBody>

          <EuiModalFooter>
            <EuiButtonEmpty onClick={close}>
              <FormattedMessage
                id='vacate-single-node-modal.cancel-vacate'
                defaultMessage='Cancel'
              />
            </EuiButtonEmpty>

            <EuiButton fill={true} disabled={true}>
              <FormattedMessage
                id='vacate-single-node-modal.move-nodes'
                defaultMessage='Move nodes'
              />
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      </EuiOverlayMask>
    )
  }
}

export default withEuiTheme(VacateNodeThroughAllocatorModal)
